/**
 * Contas de cor do `ColorPicker`: o valor por fora é sempre `#RRGGBB` (maiúsculas, como os
 * contratos da API), e por dentro o seletor trabalha em HSV — a área é saturação × brilho
 * e o trilho é o matiz, o modelo dos seletores de cor dos sistemas e dos editores.
 */

export type Hsv = {
  /** Matiz, de 0 a 360. */
  h: number;
  /** Saturação, de 0 a 1. */
  s: number;
  /** Brilho, de 0 a 1. */
  v: number;
};

/**
 * O hex digitado ou colado, normalizado: aceita com ou sem `#`, 3 ou 6 dígitos e espaços
 * nas pontas. Devolve `#RRGGBB` em maiúsculas, ou `null` se não for uma cor.
 */
export function parseHex(input: string): string | null {
  const raw = input.trim().replace(/^#/, '');

  if (/^[0-9a-f]{3}$/i.test(raw)) {
    return `#${[...raw].map((c) => c + c).join('')}`.toUpperCase();
  }

  return /^[0-9a-f]{6}$/i.test(raw) ? `#${raw}`.toUpperCase() : null;
}

function channels(hex: string): [number, number, number] {
  const n = Number.parseInt(hex.slice(1), 16);

  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function hexToHsv(hex: string): Hsv {
  const [r, g, b] = channels(hex).map((c) => c / 255) as [number, number, number];
  const max = Math.max(r, g, b);
  const delta = max - Math.min(r, g, b);
  let h = 0;

  if (delta) {
    if (max === r) h = ((g - b) / delta) % 6;
    else if (max === g) h = (b - r) / delta + 2;
    else h = (r - g) / delta + 4;
  }

  return { h: (h * 60 + 360) % 360, s: max ? delta / max : 0, v: max };
}

export function hsvToHex({ h, s, v }: Hsv): string {
  const f = (n: number) => {
    const k = (n + h / 60) % 6;

    return v - v * s * Math.max(0, Math.min(k, 4 - k, 1));
  };

  return `#${[f(5), f(3), f(1)]
    .map((c) =>
      Math.round(c * 255)
        .toString(16)
        .padStart(2, '0')
    )
    .join('')}`.toUpperCase();
}

/** A cor pura do matiz (saturação e brilho cheios), o fundo da área do seletor. */
export function hueColor(h: number): string {
  return hsvToHex({ h, s: 1, v: 1 });
}

export function clamp(value: number, min = 0, max = 1): number {
  return Math.min(max, Math.max(min, value));
}
